// Dual-handle slider over Irish chronology. Piecewise-linear so the dense CE
// centuries get more track than the sparse Neolithic millennia.
import { useEffect, useRef } from "preact/hooks";
import { dateFrom, dateTo } from "../state/store";

const ANCHORS: [number, number][] = [
  [-8000, 0.0], [-4000, 0.05], [-500, 0.15], [400, 0.28], [1100, 0.48], [1500, 0.68], [1800, 0.85], [1900, 0.92], [2030, 1.0],
];
const MIN = ANCHORS[0][0];
const MAX = ANCHORS[ANCHORS.length - 1][0];
const STEPS = 1000;
const TICKS = [-4000, -500, 1100, 1900];

function yearToSlider(y: number): number {
  if (y <= MIN) return 0;
  if (y >= MAX) return STEPS;
  for (let i = 0; i < ANCHORS.length - 1; i++) {
    const [y0, f0] = ANCHORS[i], [y1, f1] = ANCHORS[i + 1];
    if (y >= y0 && y <= y1) return Math.round((f0 + ((y - y0) / (y1 - y0)) * (f1 - f0)) * STEPS);
  }
  return STEPS;
}
function sliderToYear(v: number): number {
  const f = Math.max(0, Math.min(1, v / STEPS));
  for (let i = 0; i < ANCHORS.length - 1; i++) {
    const [y0, f0] = ANCHORS[i], [y1, f1] = ANCHORS[i + 1];
    if (f >= f0 && f <= f1) return Math.round((y0 + ((f - f0) / (f1 - f0)) * (y1 - y0)) / 50) * 50;
  }
  return f <= 0 ? MIN : MAX;
}
const short = (y: number) => (y === 0 ? "0" : y < 0 ? `-${-y}` : String(y));

export function Timeline() {
  const lo = dateFrom.value != null ? yearToSlider(dateFrom.value) : 0;
  const hi = dateTo.value != null ? yearToSlider(dateTo.value) : STEPS;
  const minRef = useRef<HTMLInputElement>(null);
  const maxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (minRef.current) minRef.current.value = String(lo);
    if (maxRef.current) maxRef.current.value = String(hi);
  }, [lo, hi]);

  const onMin = (e: Event) => {
    let v = Number((e.target as HTMLInputElement).value);
    if (v > hi) { v = hi; (e.target as HTMLInputElement).value = String(v); }
    dateFrom.value = v === 0 ? null : sliderToYear(v);
  };
  const onMax = (e: Event) => {
    let v = Number((e.target as HTMLInputElement).value);
    if (v < lo) { v = lo; (e.target as HTMLInputElement).value = String(v); }
    dateTo.value = v === STEPS ? null : sliderToYear(v);
  };
  const readout = dateFrom.value == null && dateTo.value == null
    ? "Any date"
    : `${dateFrom.value != null ? short(dateFrom.value) : "…"} → ${dateTo.value != null ? short(dateTo.value) : "…"}`;

  return (
    <div class="timeline">
      <div class="timeline-value" aria-live="polite">{readout}</div>
      <div class="timeline-track">
        <div class="timeline-rail" />
        <div class="timeline-fill" style={{ left: `${(lo / STEPS) * 100}%`, width: `${Math.max(0, hi - lo) / STEPS * 100}%` }} />
        <input ref={minRef} type="range" min={0} max={STEPS} step={1} defaultValue={String(lo)} onInput={onMin} aria-label="Earliest year" />
        <input ref={maxRef} type="range" min={0} max={STEPS} step={1} defaultValue={String(hi)} onInput={onMax} aria-label="Latest year" />
      </div>
      <div class="timeline-ticks" aria-hidden="true">
        {TICKS.map((y) => <span class="timeline-tick" style={{ left: `${(yearToSlider(y) / STEPS) * 100}%` }}>{short(y)}</span>)}
      </div>
      <div class="row-2">
        <input type="number" step={50} placeholder="e.g. -3500" value={dateFrom.value ?? ""} onInput={(e) => { const v = (e.target as HTMLInputElement).value; dateFrom.value = v === "" ? null : Number(v); }} aria-label="From year" />
        <span class="sep">→</span>
        <input type="number" step={50} placeholder="e.g. 1700" value={dateTo.value ?? ""} onInput={(e) => { const v = (e.target as HTMLInputElement).value; dateTo.value = v === "" ? null : Number(v); }} aria-label="To year" />
      </div>
    </div>
  );
}
